import { FC, useMemo } from 'react';

import { Typography } from '@mui/material';
import { useField } from 'formik';
import { useRecoilValue } from 'recoil';
import { AdditionalTariffChargeType } from 'types/graphql';

import { complianceChargeDefaultsAtom } from 'src/atoms';
import { Flex } from 'src/components/Flex';
import NPICurrencyInputField from 'src/components/FormikComponents/NPICurrencyInputField/NPICurrencyInputField';
import { useAuthContext } from 'src/contexts/AuthProvider';

type Charges = {
    label: string;
    hint: string;
    note?: string;
    amount: number;
    name: string;
    type: AdditionalTariffChargeType;
    highestRate: number;
    lowestRate: number;
};

interface ComplianceDisplay {
    heading: string;
    name: string;
    editTransactionCharges: boolean;
}

export const ComplianceDisplay: FC<ComplianceDisplay> = ({
    heading,
    name,
    editTransactionCharges,
}) => {
    const { usersCurrency } = useAuthContext();
    const chargesDefault = useRecoilValue(complianceChargeDefaultsAtom);

    const [{ value }] = useField(name);

    const chargeNames = useMemo(() => {
        if (value) {
            return Object.keys(value);
        }
        return [];
    }, [value]);

    return (
        <Flex flexDirection="column" gap="24px">
            <Typography fontSize="18px" fontWeight="500" lineHeight="26px" color="#F18F34">
                {heading}
            </Typography>
            {chargeNames.map((chargeName) => {
                const charge = chargesDefault.find((el) => {
                    return el.name === chargeName;
                });
                const fieldName = `${name}.${charge?.name}`;

                return (
                    <Flex key={charge?.label} flex={1} justifyContent="space-between" gap="24px">
                        <Flex flexDirection="column" flex={0.7} gap="8px">
                            <Flex
                                fontSize="14px"
                                fontWeight="400"
                                lineHeight="21px"
                                alignItems="flex-start"
                            >
                                <Typography color="#F6B478" width="max-content">
                                    {charge?.label}
                                </Typography>
                                <Typography color="#434341">{` - ${charge?.hint}`}</Typography>
                            </Flex>
                            {charge?.note && (
                                <Flex
                                    fontSize="11px"
                                    fontWeight="400"
                                    lineHeight="15px"
                                    alignItems="flex-start"
                                    gap="8px"
                                >
                                    <Typography color="#686867" fontWeight="700">
                                        Note.
                                    </Typography>
                                    <Typography color="#8E8E8D" fontWeight="400">
                                        {` ${charge?.note}`}
                                    </Typography>
                                </Flex>
                            )}
                        </Flex>
                        <Flex alignItems="center" flex={0.3} gap="24px">
                            <NPICurrencyInputField
                                fieldName={fieldName}
                                disabled={
                                    !editTransactionCharges ||
                                    (editTransactionCharges && charge?.type === 'FIXED')
                                }
                                min={charge?.lowestRate}
                                max={charge?.highestRate}
                            />
                            <Typography color="#575757" fontSize="14px">
                                Per Month
                            </Typography>
                        </Flex>
                    </Flex>
                );
            })}
        </Flex>
    );
};
